// src/pages/Privacy.jsx
import React from 'react';
import '../styles/components/privacy.css';

function Privacy({ region: _region }) {
  return (
    <main className="legal-page legal-page--privacy">
      <div className="container">
        <h1>Privacy Policy</h1>

        <p>
          At Pure Health Nexus, we care about your privacy and how your personal and nutrition data are used.
          This page explains, in more detail, how we handle your information when you use our website and apps.
        </p>

        <h2>1. Who we are</h2>
        <p>
          Pure Health Nexus, part of the Asset Legacy family, helps you understand your nutrition and eating patterns.
          For EU/EEA users, Pure Health Nexus is based in Sweden and acts as the data controller for the personal
          data collected and processed through this website and our apps.
        </p>
        <p>
          If you have questions about this notice or how we handle your data, you can contact us at&nbsp;
          <a href="mailto:info@purehealthnexus.com">info@purehealthnexus.com</a>.
        </p>

        <h2>2. What data we collect</h2>
        <p>Depending on how you use the service, we may process:</p>
        <ul>
          <li>
            <strong>Account and profile data</strong>  such as your name or nickname, email address, language,
            and other information you choose to add to your profile.
          </li>
          <li>
            <strong>Nutrition and meal data</strong>  foods you log, photos text of meals, voice notes, text descriptions,
            time of day, and AI-generated nutrition breakdowns and insights.
          </li>
          <li>
            <strong>Usage and device data</strong>  technical data from your browser or device (such as 
            device type, browser type, and basic usage logs) to keep the service secure and improve performance.
          </li>
          <li>
            <strong>Support and communication data</strong>  messages you send to us, feedback, and responses to surveys.
          </li>
        </ul>
        <p>
          Because meal and nutrition data can be linked back to your account, we treat it as personal data under the GDPR.
        </p>

        <h2>3. How and why we use your data</h2>
        <p>We use your data to:</p>
        <ul>
          <li>
            <strong>Provide and improve the service</strong>  operate the app, calculate nutrition from your logs,
            show dashboards and insights, and improve features over time.
          </li>
          <li>
            <strong>Use AI to analyse your meals</strong>  when you submit meal photos, voice notes or text descriptions,
            we may use AI services to identify foods, estimate portion sizes and derive nutrients (energy, macros,
            vitamins, minerals, etc.).
          </li>
          <li>
            <strong>Personalise your experience</strong>  apply your profile information and logged data to show relevant
            feedback, such as estimated energy balance or nutrient gaps.
          </li>
          <li>
            <strong>Communicate with you</strong>  send important service messages, security alerts, and responses to your
            support requests. With your consent, we may send optional updates or product information.
          </li>
          <li>
            <strong>Keep the service secure and comply with law</strong>  monitor for misuse, protect against fraud or abuse,
            and meet our legal obligations.
          </li>
        </ul>
        <p>Our main legal bases under the GDPR are:</p>
        <ul>
          <li>
            <strong>Performance of a contract</strong>  to provide the service you sign up for.
          </li>
          <li>
            <strong>Legitimate interests</strong>  for security, improvement, and basic analytics, where these interests
            are not overridden by your rights and freedoms.
          </li>
          <li>
            <strong>Consent</strong>  for certain cookies, marketing communication and, where required, specific data uses.
          </li>
        </ul>

        <h2>4. How our AI meal analysis works</h2>
        <p>We use AI to understand what you eat, not who you are.</p>
        <ul>
          <li>
            When we send data to external AI providers, we send only the meal content needed for analysis, such as cropped
            meal images or text describing the food.
          </li>
          <li>
            We do not include direct profile identifiers such as your name, email address, personal identity number, or
            other account details in AI requests.
          </li>
          <li>
            The AI providers do not have access to your user profile in our systems.
          </li>
        </ul>
        <p>
          Your profile data and meal logs are stored in separate systems with separate access rules. Our own service joins
          the results so that:
        </p>
        <ul>
          <li>The AI only sees the food information it needs to analyse; and</li>
          <li>Your profile system does not expose your identity details to the AI.</li>
        </ul>
        <p>
          Even when we remove direct identifiers, we handle meal-related data as personal data because it can still be
          linked back to you through your account.
        </p>
        <p>
          We work with AI providers as data processors under the GDPR, use written data processing agreements, and require
          them to protect your data, follow our instructions, and not use your information for their own marketing.
        </p>

        <h2>5. Sharing and transfers</h2>
        <p>
          We do not sell your personal data and we do not share it with third parties for their own marketing purposes.
        </p>
        <p>We may share your data with:</p>
        <ul>
          <li>
            <strong>Service providers (processors)</strong> who help us run our service, such as hosting, analytics,
            security and AI providers but never personal data related to nutrition and meal data or other personal data.
          </li>
          <li>
            <strong>Professional advisers</strong> such as lawyers and accountants, where necessary.
          </li>
          <li>
            <strong>Authorities or other parties</strong> where required by law or to protect our legal rights (for example,
            in relation to fraud or security incidents).
          </li>
        </ul>
        <p>
          If we transfer personal data outside the EU/EEA, we will follow GDPR requirements, for example by using EU
          Standard Contractual Clauses or other recognised safeguards.
        </p>

        <h2>6. How long we keep your data</h2>
        <p>We keep personal data only for as long as needed for the purposes described in this notice:</p>
        <ul>
          <li>
            <strong>Account and profile data</strong>  for as long as you have an account.
          </li>
          <li>
            <strong>Nutrition and meal data</strong>  for as long as it is needed to provide your insights and for a
            reasonable period afterwards, unless you delete it earlier.
          </li>
          <li>
            <strong>Technical logs</strong>  for a limited period for security, troubleshooting and audit purposes.
          </li>
        </ul>
        <p>
          You can usually delete individual logs in the app. If you delete your account, we will remove or anonymise your
          personal data within a reasonable time, except where we must keep some information for legal or accounting reasons.
        </p>

        <h2>7. Your rights</h2>
        <p>Under the GDPR, you have the right to:</p>
        <ul>
          <li>Access your personal data.</li>
          <li>Correct inaccurate or incomplete data.</li>
          <li>Delete your data in certain situations (“right to be forgotten”).</li>
          <li>Restrict or object to some types of processing.</li>
          <li>Receive your data in a portable format where applicable.</li>
          <li>Withdraw consent at any time, where processing is based on consent.</li>
        </ul>
        <p>
          You can exercise many of these rights directly in the app (for example, by editing your profile or deleting logs).
          For other requests, contact us at&nbsp;
          <a href="mailto:info@purehealthnexus.com">info@purehealthnexus.com</a>. We may need to verify your identity
          before responding.
        </p>
        <h2>8. Cookies and similar technologies</h2>
        <p>
          We use cookies and similar technologies to make the site work and keep you logged in, remember your preferences
          and settings, understand how the site is used so we can improve it, and track referrals and aggregated usage
          patterns for analytics.
        </p>
        <p>
          Where required, we ask for your consent to non-essential cookies (such as analytics and marketing cookies) via
          our cookie banner. You can change your preferences at any time in the banner or through your browser settings.
          For more detail, please see our separate Cookie Policy.
        </p>

        <h2>9. Changes to this notice</h2>
        <p>
          We may update this Privacy Policy from time to time. If the changes are significant, we will provide a clear
          notice on the site or in the app. The “last updated” date will always tell you when this policy was last revised.
        </p>
      </div>
    </main>
  );
}

export default Privacy;
